export const qagentChat = {
  header: {
    newChat: 'New Chat',
    resources: 'Resources',
    exportConversation: 'Export to Word',
    exportConversationHint: 'Download this conversation as a Word document',
  },
  export: {
    defaultTitle: 'QAgent conversation',
    dateLine: (date: string) => `Exported from Queri on ${date}`,
    youLabel: 'You',
    failed: "Couldn't create the Word document. Please try again.",
  },
  greeting: {
    welcome: (firstName: string) => `Welcome, ${firstName}`,
    hello: 'Hello there',
    prompt: 'What legal matters are we tackling today?',
  },
  messages: {
    loading: 'Loading messages…',
  },
  input: {
    placeholderWithMentions:
      'Message QAgent... (type @ to mention, / for commands)',
    placeholderNoMentions: 'Message QAgent... (type / for commands)',
    sendAriaLabel: 'Send message',
    sendTitle: 'Send message (Enter)',
    failed: 'Message failed to send',
    escToStopPrefix: 'Press',
    escToStopSuffix: 'to stop generating',
    escKey: 'Esc',
  },
  projectIndicator: {
    fileCount: (count: number) => `${count} file${count !== 1 ? 's' : ''}`,
    clearTitle: 'Clear messages and active context',
    clear: 'Clear',
    emailAttachments: 'Email attachments',
  },
  picker: {
    selectFilePrompt: 'Select a file above…',
    fileChipFallback: 'File',
    titles: {
      review: 'Review a document',
      compare: 'Compare two documents',
      summarize: 'Summarize a document',
      'redline-summary': 'Summarize redline changes',
      default: 'Select a document',
    },
    filesSelectTwo: 'Files (select 2)',
    standardAgreements: 'Our standard agreements',
    file: 'File',
    noFilesAvailable: 'No files available',
    playbook: 'Playbook',
    optional: '(optional)',
    startComparison: 'Start Comparison',
    compareWithStandard: 'Compare with standard',
    selectFiles: (remaining: number) =>
      `Select ${remaining} file${remaining === 1 ? '' : 's'}`,
    startReview: 'Start Review',
    startSummary: 'Start Summary',
    startAnalysis: 'Start Analysis',
    selectFileToContinue: 'Select a file to continue',
  },
  suggestions: {
    heading: 'AI Suggestions',
    pendingCount: (count: number) => `${count} pending`,
    rejectAll: 'Reject All',
    acceptAll: 'Accept All',
  },
  dialogs: {
    upload: {
      title: 'Uploading file…',
      progress: (percent: number) => `${percent}% complete`,
    },
    linkPlaybook: {
      title: 'Link Playbook',
      description:
        'Select a playbook to link to this project. Linked playbooks can be used to guide AI responses.',
      label: 'Playbook',
      placeholder: 'Select a playbook',
      noneAvailable: 'No available playbooks',
      linking: 'Linking…',
      submit: 'Link Playbook',
    },
    deleteFile: {
      title: 'Delete File',
      description: (name: string) =>
        `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      deleting: 'Deleting…',
    },
    unlinkPlaybook: {
      title: 'Unlink Playbook',
      description: (name: string) =>
        `Are you sure you want to unlink "${name}" from this project?`,
      unlinking: 'Unlinking…',
      submit: 'Unlink',
    },
  },
  dropZone: {
    withProject: (projectName: string) => `Drop to attach to ${projectName}`,
    noProject: 'Drop to attach to this project',
  },
  toasts: {
    standardAgreementFailed: 'Could not add the standard agreement to this project',
    loadChatFailed: 'Failed to load chat',
    exportingContract: 'Exporting contract…',
    exported: (fileName: string) => `Exported: ${fileName}`,
    exportContractFailed: 'Failed to export contract',
    noFileToExport: 'No file available to export',
    exportingDocument: 'Exporting document…',
    exportFileFailed: 'Failed to export file',
    exportedWithSkipped: (applied: number, skipped: number) =>
      `${applied} changes applied, ${skipped} could not be applied because their original text was not found in the document. They were left out, so review the document.`,
    exportNothingApplied: (skipped: number) =>
      `No changes were applied: the original text of ${skipped} suggestions was not found in the document.`,
    requiresProject: 'This command requires a project to be attached',
    requiresFiles: 'This command requires files in the project',
    enterDocType: 'Please enter a document type',
    initContractFailed: 'Failed to initialize contract generation',
    startContractFailed: 'Failed to start contract generation',
    contractStateNotFound: 'Contract state not found',
    processDetailsFailed: 'Failed to process contract details',
    noContractId: 'No contract ID returned',
    processingDetails: 'Processing details',
    startSectionFailed: 'Failed to start section generation',
    generating: 'generating',
    contractGenerationFailed: 'Contract generation failed',
    projectAttached: 'Project attached',
    projectDetached: 'Project detached',
    updateProjectFailed: 'Failed to update project',
    fileUploaded: (name: string) => `File "${name}" uploaded successfully`,
    uploadFileFailed: 'Failed to upload file',
    uploadFileError: 'Error uploading file',
    playbookLinked: 'Playbook linked successfully',
    linkPlaybookFailed: 'Failed to link playbook',
    linkPlaybookError: 'Error linking playbook',
    fileDeleted: (name: string) => `File "${name}" deleted`,
    deleteFileFailed: 'Failed to delete file',
    deleteFileError: 'Error deleting file',
    playbookUnlinked: (name: string) => `Playbook "${name}" unlinked`,
    unlinkPlaybookFailed: 'Failed to unlink playbook',
    unlinkPlaybookError: 'Error unlinking playbook',
    suggestionAccepted: 'Suggestion accepted',
    suggestionApplyFailed: (count: number) =>
      count === 1
        ? "1 accepted change couldn't be placed in the document automatically — review it in the document view."
        : `${count} accepted changes couldn't be placed in the document automatically — review them in the document view.`,
    acceptSuggestionFailed: 'Failed to accept suggestion',
    acceptSuggestionError: 'Error accepting suggestion',
    suggestionRejected: 'Suggestion rejected',
    rejectSuggestionFailed: 'Failed to reject suggestion',
    rejectSuggestionError: 'Error rejecting suggestion',
    suggestionUndone: 'Suggestion undone',
    undoSuggestionFailed: 'Failed to undo suggestion',
    undoSuggestionError: 'Error undoing suggestion',
    suggestionsAccepted: (count: number) => `${count} suggestions accepted`,
    acceptSuggestionsFailed: 'Failed to accept suggestions',
    acceptSuggestionsError: 'Error accepting suggestions',
    suggestionsRejected: (count: number) => `${count} suggestions rejected`,
    rejectSuggestionsFailed: 'Failed to reject suggestions',
    rejectSuggestionsError: 'Error rejecting suggestions',
    fileNotFound: 'File not found',
    preparingDocument: 'Preparing document…',
    downloaded: (fileName: string) => `Downloaded: ${fileName}`,
    downloadDocumentFailed: 'Failed to download document',
    regenerateFailed: 'Failed to regenerate',
    sendMessageFailed: 'Failed to send message',
    contractSaved: (fileName: string, projectName: string) =>
      `${fileName} saved to ${projectName}`,
    contractSavedNewProject: (fileName: string, projectName: string) =>
      `Created project "${projectName}" and saved ${fileName} to it`,
    saveContractFailed: "Couldn't save the contract to project files",
  },
  contractForm: {
    title: 'Document details',
    required: (filled: number, total: number) => `${filled}/${total} required`,
    parties: 'Parties',
    additionalDetails: 'Additional details',
    optional: 'optional',
    generate: 'Generate document',
    moreDetails: (count: number) => `More details (${count})`,
    fewerDetails: 'Fewer details',
    submitted: 'Details used for this document',
  },
  projectChat: {
    loadFailed: 'Failed to load project chat',
    unexpectedError: 'An unexpected error occurred',
    goHome: 'Go back home',
    loading: 'Loading project...',
  },
  mentionMenu: {
    title: '@ Mention',
    playbooks: 'Playbooks',
    files: 'Files',
    knowledge: 'Knowledge',
    noPlaybooks: 'No playbooks',
    noFiles: 'No files',
    soon: 'Soon',
    unavailable: 'Unavailable',
    noItems: 'No items available',
    comingSoon: 'Coming Soon',
    knowledgeHint: 'Search across all your documents',
  },
  slashCommands: {
    comingSoon: 'Coming soon',
    requiresProject: 'Requires project',
    requiresFiles: 'Requires files',
    // Display text for the built-in commands, keyed by command id (the id itself is what users type)
    commands: {
      review: { name: 'Review', description: 'Legal review of a document' },
      compare: { name: 'Compare', description: 'Compare two documents side by side' },
      search: { name: 'Search', description: 'Research across documents and the web' },
      summarize: { name: 'Summarize', description: 'Executive summary of a document' },
      'redline-summary': {
        name: 'Redline Summary',
        description: 'Summarize tracked changes and comments',
      },
      generate: { name: 'Generate', description: 'Generate a new legal document' },
      export: { name: 'Export', description: 'Export document with accepted changes' },
      clear: { name: 'Clear', description: 'Clear conversation history' },
    } as Record<string, { name: string; description: string }>,
  },
  contract: {
    writingProgress: (written: number, total: number) => `${written} of ${total} sections written`,
    writingSection: 'Writing…',
    generatePrefix: (docType: string) => `Generate a ${docType}`,
    generateWithDescription: (docType: string, description: string) =>
      `Generate a ${docType}: ${description}`,
    party1: 'document 1',
    party2: 'document 2',
    compare: (file1: string, file2: string) =>
      `Compare @[${file1}] and @[${file2}]`,
    compareWithStandard: (file: string, standardFile: string, standardName: string) =>
      `Compare @[${file}] against our standard agreement @[${standardFile}] ("${standardName}"). Report the deviations from our standard, the risk to us and suggested positions.`,
    documentFallback: 'document',
    reviewWithPlaybook: (fileName: string, pbName: string) =>
      `Review @[${fileName}] using the @[${pbName}] playbook`,
    reviewThorough: (fileName: string) =>
      `Perform a thorough legal review of @[${fileName}]`,
    summarize: (fileName: string) => `Summarize @[${fileName}]`,
    redlineSummary: (fileName: string) =>
      `Summarize the redline changes and comments in @[${fileName}]`,
    analyze: (fileName: string) => `Analyze @[${fileName}]`,
    playbookFallback: 'Playbook',
    fileFallback: 'File',
    generationFailedTitle: 'The contract could not be finished',
    generationFailedBody: 'Something went wrong while writing it, usually a temporary problem on our side. Try again to generate it from the details you entered.',
    generationRetry: 'Try again',
    ready: (docType: string) => `${docType} ready`,
    sectionsGenerated: (count: number) =>
      `${count} section${count === 1 ? '' : 's'} generated`,
    savingToProject: 'Saving to project files…',
    savedToProject: (projectName: string) => `Saved to ${projectName}`,
    savedToProjectFiles: 'Saved to your project files',
    saveFailed: "Couldn't save to project files",
    notSaved: 'Not saved to project files',
    saveToProject: 'Save to project',
    retrySave: 'Retry',
    openDocument: 'Open document',
    showSections: 'Show sections',
    hideSections: 'Hide sections',
    rulesApplied: 'Playbook rules applied',
    rulesAppliedDescription: 'These rules from your playbook influenced this contract:',
    ruleConfidence: (confidence: string) => `Applied with ${confidence} confidence`,
    showInDocument: 'Click to show in the document',
  },
};

export type QagentChatDict = typeof qagentChat;
